import * as React from 'react'
import { IsMobileContext } from '../../common/Context'
import { DropdownProps, DropdownState, ActionTypes, DropdownActionTypes } from './types'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import AccordionItem from '../Accordion/AccordionItem'
import './style.scss'

const Dropdown: React.FC<DropdownProps> = ({
  field,
  placeholder,
  options,
  required,
  onChange,
  className,
  revalidate,
}) => {
  const {t} = useTranslation()
  const { isMobile } = React.useContext(IsMobileContext)
  const dropdownElement = React.useRef<HTMLDivElement>(null)

  const nbspValue =  String.fromCharCode(160)
  const dropdownInitialState: DropdownState = {
    value: placeholder ? nbspValue : options[0],
    isValid: placeholder ? !required : true,
    active: false,
    touched: placeholder ? false : true,
  }

  const dropdownReducer = (state: DropdownState, action: DropdownActionTypes) => {
    switch (action.type) {
    case ActionTypes.DROPDOWN_CHANGE:
      return {
        ...state,
        value: action.value,
        isValid: action.isValid,
        touched: true,
        active: false,
      }
    case ActionTypes.DROPDOWN_FOCUS:
      return {
        ...state,
        active: true,
      }
    case ActionTypes.DROPDOWN_BLUR:
      return {
        ...state,
        touched: true,
        active: false,
      }
    default:
      return state
    }
  }

  const [dropdownState, dispatch] = React.useReducer(dropdownReducer, dropdownInitialState)
  const [bgClass, setBgClass] = React.useState('bgBlurred')

  React.useEffect(() => {
    const handleClickOutside = function (this: Document, event: MouseEvent) {
      if (dropdownState.active
        && dropdownElement.current 
        && !dropdownElement.current.contains(event.target as HTMLElement)
      ) {
        dispatch({ type: ActionTypes.DROPDOWN_BLUR })
      }
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('click', handleClickOutside, true)
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('click', handleClickOutside, true)
      }
    }
  }, [dropdownState.active])

  React.useEffect(() => {
    if (revalidate) {
      dispatch({ type: ActionTypes.DROPDOWN_BLUR })
    }
  }, [revalidate])

  React.useEffect(() => {
    if (dropdownState.touched) {
      onChange(field, dropdownState.value, dropdownState.isValid)
    }
  }, [dropdownState, onChange, field])

  const toggleHandler = (active: boolean) => {
    if (active) {
      dispatch({ type: ActionTypes.DROPDOWN_FOCUS })
    } else {
      dispatch({ type: ActionTypes.DROPDOWN_BLUR })
    }
  }

  const changeHandler = (value: string) => {
    setBgClass('bgFocused')
    dispatch({ type: ActionTypes.DROPDOWN_CHANGE, value, isValid: true })
  }

  const iconSize = isMobile ? '4vw' : '1vw'
  const validatedError = !dropdownState.isValid && dropdownState.touched

  const dropdownOptions = (
    <ul>
      {options.map((option, index) => (
        <li 
          className={`cursor-pointer hover:underline
            ${index < options.length - 1 ? 'mb-mobile-xxxs lg:mb-xxxs' : ''} `}
          key={index}
          onClick={() => changeHandler(option)}
        >
          {option}
        </li>
      ))}
    </ul>
  )

  return (
    <div className={`Dropdown ${className || ''}`} ref={dropdownElement}>
      <div 
        className={`FontS w-full
          ${bgClass} hover:bgFocused
          flex items-center
          ${validatedError ? 'border-b border-accent -mb-px' : ''}`}
        style={{padding: iconSize}}
      >
        <div className="flex-grow relative">
          <AccordionItem
            title={dropdownState.value}
            content=""
            className="FontS"
            contentClassName="mt-mobile-xxs lg:mt-xxs"
            onToggle={toggleHandler}
            initiallyActive={dropdownState.active}
          >
            {dropdownOptions}
          </AccordionItem>
          {dropdownState.value.trim().length === 0 ? (
            <div className="absolute top-0 pointer-events-none">
              {placeholder}
              {!required && <span className="text-gray-50"> ({t('common:optional')})</span>}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default Dropdown
